import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, from } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../global-message';
import { HttpResponseStatus } from '../../../global-message/models/response-status.model';
import { GlobalMessageActions } from '../../../global-message/store';
import { CartModification } from '../../../model/cart.model';
import { normalizeHttpError, withdrawOn } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { ShoppingListEntryConnector } from '../../connectors/shopping-list-entry-connector';
import { UserActions } from '../actions/index';
import { ShoppingListEntryActionTypes } from '../actions/shopping-list-entry.action';
import { LoadShoppingList } from '../actions/shopping-list.action';

@Injectable()
export class ShoppingListEntryEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  addEntry$: Observable<
    UserActions.AddShoppingListEntrySuccess | UserActions.AddShoppingListEntryFail | UserActions.SearchMiniShoppingLists
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.AddShoppingListEntry),
      map((action: UserActions.AddShoppingListEntry) => action.payload),
      concatMap((payload) => {
        return this.connector
          .add(
            payload.userId,
            payload.code,
            payload.articleCode,
            payload.quantity,
            payload.unit,
            payload.note,
            payload.displayNumber
          )
          .pipe(
            map(
              (cartModification: CartModification) =>
                new UserActions.AddShoppingListEntrySuccess({
                  ...payload,
                  ...(cartModification as Required<CartModification>),
                })
            ),
            catchError((error) =>
              from([
                new UserActions.AddShoppingListEntryFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
                new UserActions.SearchMiniShoppingLists(payload),
              ])
            )
          );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  handleSuccessForAddEntry$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.AddShoppingListEntrySuccess),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: { key: 'shoppingLists.addShoppingListEntrySuccess_message' },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      )
    )
  );

  handleFailureForAddEntry$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.AddShoppingListEntryFail),
      map((action: UserActions.AddShoppingListEntryFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'shoppingLists.addShoppingListEntryFailure_message',
              params: {
                articleNumber: action.payload.articleCode,
              },
              errorDetails: action.payload.error?.details.map((detail) => ({
                ...detail,
                translationKey:
                  detail.type === 'MixedNonAndPrePrintedLabelError' ? 'common.error.mixednonandpreprintedlabel_message' : '',
              })),
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  removeEntry$: Observable<
    UserActions.RemoveShoppingListEntrySuccess | UserActions.RemoveShoppingListEntryFail | UserActions.SearchMiniShoppingLists
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.RemoveShoppingListEntry),
      map((action: UserActions.RemoveShoppingListEntry) => action.payload),
      concatMap((payload) =>
        this.connector.remove(payload.userId, payload.code, payload.entry).pipe(
          map(() => {
            return new UserActions.RemoveShoppingListEntrySuccess({
              ...payload,
            });
          }),
          catchError((error) =>
            from([
              new UserActions.RemoveShoppingListEntryFail({
                ...payload,
                error: normalizeHttpError(error),
              }),
              new UserActions.SearchMiniShoppingLists(payload),
            ])
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForRemoveEntry$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.RemoveShoppingListEntryFail),
      map((action: UserActions.RemoveShoppingListEntryFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'shoppingLists.shoppingListNotFound_message',
                params: {
                  shoppingListCode: action.payload.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'shoppingLists.removeShoppingListEntryFailure_message',
                params: {
                  entryNumber: +action.payload.entry.entryNumber + 1,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          }
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  removeEntries$: Observable<UserActions.RemoveShoppingListEntriesSuccess | UserActions.RemoveShoppingListEntriesFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingListEntryActionTypes.RemoveShoppingListEntries),
        map((action: UserActions.RemoveShoppingListEntries) => action.payload),
        concatMap((payload) =>
          this.connector.removeMultiple(payload.userId, payload.code, payload.entries).pipe(
            map(() => {
              return new UserActions.RemoveShoppingListEntriesSuccess({
                ...payload,
              });
            }),
            catchError((error) =>
              from([
                new UserActions.RemoveShoppingListEntriesFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
              ])
            )
          )
        ),
        withdrawOn(this.contextChange$)
      )
    );

  handleFailureForRemoveEntries$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.RemoveShoppingListEntriesFail),
      map((action: UserActions.RemoveShoppingListEntriesFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'shoppingLists.removeShoppingListEntriesFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  handleSuccessForRemoveEntries$: Observable<LoadShoppingList> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.RemoveShoppingListEntriesSuccess),
      map((action: UserActions.RemoveShoppingListEntriesFail) => {
        return new UserActions.LoadShoppingList({
          userId: action.payload.userId,
          code: action.payload.code,
        });
      })
    )
  );

  updateEntry$: Observable<
    UserActions.UpdateShoppingListEntrySuccess | UserActions.UpdateShoppingListEntryFail | UserActions.SearchMiniShoppingLists
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.UpdateShoppingListEntry),
      map((action: UserActions.UpdateShoppingListEntry) => action.payload),
      concatMap((payload) => {
        return this.connector
          .update(
            payload.userId,
            payload.code,
            payload.entry,
            payload.quantity,
            payload.unit,
            payload.note,
            payload.statisticsCode
          )
          .pipe(
            map(() => {
              return new UserActions.UpdateShoppingListEntrySuccess({
                ...payload,
              });
            }),
            catchError((error) =>
              from([
                new UserActions.UpdateShoppingListEntryFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
                new UserActions.SearchMiniShoppingLists(payload),
              ])
            )
          );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForUpdateEntry$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.UpdateShoppingListEntryFail),
      map((action: UserActions.UpdateShoppingListEntryFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'shoppingLists.shoppingListNotFound_message',
                params: {
                  shoppingListCode: action.payload.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'shoppingLists.updateShoppingListEntryFailure_message',
                params: {
                  entryNumber: +action.payload.entry.entryNumber + 1,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          }
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  replaceEntry$: Observable<
    UserActions.ReplaceShoppingListEntrySuccess | UserActions.ReplaceShoppingListEntryFail | UserActions.SearchMiniShoppingLists
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.ReplaceShoppingListEntry),
      map((action: UserActions.ReplaceShoppingListEntry) => action.payload),
      concatMap((payload) =>
        this.connector
          .replace(payload.userId, payload.code, payload.entry, payload.articleNumber, payload.quantity, payload.unit)
          .pipe(
            map(() => {
              return new UserActions.ReplaceShoppingListEntrySuccess({
                ...payload,
              });
            }),
            catchError((error) =>
              from([
                new UserActions.ReplaceShoppingListEntryFail({
                  ...payload,
                  error: normalizeHttpError(error),
                }),
                new UserActions.SearchMiniShoppingLists(payload),
              ])
            )
          )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForReplaceEntry$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.ReplaceShoppingListEntryFail),
      map((action: UserActions.ReplaceShoppingListEntryFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'shoppingLists.shoppingListNotFound_message',
                params: {
                  shoppingListCode: action.payload.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'shoppingLists.updateShoppingListEntryFailure_message',
                params: {
                  entryNumber: +action.payload.entry.entryNumber + 1,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          }
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  handleSuccessForReplaceEntry$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListEntryActionTypes.ReplaceShoppingListEntrySuccess),
      map((_) => {
        return new GlobalMessageActions.AddMessage({
          text: {
            key: 'shoppingLists.replaceEntrySuccess_message',
          },
          type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
        });
      })
    )
  );

  constructor(private actions$: Actions, private connector: ShoppingListEntryConnector) {}
}
